import BrowserBehavior from 'amazon-chime-sdk-js/build/browserbehavior/BrowserBehavior';
import DefaultBrowserBehavior from 'amazon-chime-sdk-js/build/browserbehavior/DefaultBrowserBehavior';
import { getParser, Parser } from 'bowser';

export const getJoinToken = () => {
  const cachedToken = sessionStorage.getItem('joinToken');
  const pathMatch = window.location.pathname.match(/^\/(join|j|dev-join)\/([^\\/]+)$/);
  const urlToken = pathMatch ? pathMatch[2] : '';
  return urlToken || cachedToken || '';
};

export const getDeeplinkCode = () => {
  const pathMatch = window.location.pathname.match(/^\/(deeplink)\/([^\\/]+)$/);
  const urlCode = pathMatch ? pathMatch[2] : '';
  return urlCode;
};

export const getAthenaContextId = () => {
  const token = getJoinToken();
  return parseInt(token && token.split('.')[1]) || 0;
};

export const getVisitUrl = () => {
  return window.location.origin + '/join/' + sessionStorage.getItem('joinToken');
};

// Unsupported Browser Code

export enum UnsupportedBrowserScenario {
  UNSUPPORTED_CHROME = 'UNSUPPORTED_CHROME',
  UNSUPPORTED_FIREFOX = 'UNSUPPORTED_FIREFOX',
  UNSUPPORTED_EDGE = 'UNSUPPORTED_EDGE',
  UNSUPPORTED_SAFARI = 'UNSUPPORTED_SAFARI',
  UNSUPPORTED_SAMSUNG_INTERNET = 'UNSUPPORTED_SAMSUNG_INTERNET',
  UNSUPPORTED_OPERA = 'UNSUPPORTED_OPERA',
  CHROME_ON_IOS = 'CHROME_ON_IOS',
  INTERNET_EXPLORER = 'INTERNET_EXPLORER',
  UNSUPPORTED_WINDOWS_BROWSER = 'UNSUPPORTED_WINDOWS_BROWSER',
  UNSUPPORTED_MACOS_BROWSER = 'UNSUPPORTED_MACOS_BROWSER',
  UNSUPPORTED_ANDROID_BROWSER = 'UNSUPPORTED_ANDROID_BROWSER',
  UNSUPPORTED_IOS_BROWSER = 'UNSUPPORTED_IOS_BROWSER',
}

const unSupportedVersionScenarios = [
  UnsupportedBrowserScenario.UNSUPPORTED_CHROME,
  UnsupportedBrowserScenario.UNSUPPORTED_FIREFOX,
  UnsupportedBrowserScenario.UNSUPPORTED_EDGE,
  UnsupportedBrowserScenario.UNSUPPORTED_SAFARI,
  UnsupportedBrowserScenario.UNSUPPORTED_SAMSUNG_INTERNET,
  UnsupportedBrowserScenario.UNSUPPORTED_OPERA,
];

const unsupportedBrowserOfOSScenarios = [
  UnsupportedBrowserScenario.UNSUPPORTED_WINDOWS_BROWSER,
  UnsupportedBrowserScenario.UNSUPPORTED_MACOS_BROWSER,
  UnsupportedBrowserScenario.UNSUPPORTED_ANDROID_BROWSER,
  UnsupportedBrowserScenario.UNSUPPORTED_IOS_BROWSER,
];

const supportedOS = ['Windows', 'macOS', 'iOS', 'Android', 'Chrome OS'];
const browserName: { [id: string]: string } = {
  chrome: 'Google Chrome',
  crios: 'Google Chrome',
  edge: 'Microsoft Edge',
  'edge-chromium': 'Microsoft Edge',
  electron: 'Electron',
  firefox: 'Mozilla Firefox',
  ie: 'Internet Explorer',
  ios: 'Safari',
  safari: 'Safari',
  samsung: 'Samsung Internet',
  opera: 'Opera',
};

const getBrowserName = () => {
  const browserClass: BrowserBehavior = new DefaultBrowserBehavior();
  return browserName[browserClass.name()];
};

interface UnsupportedBrowserScenarioMap {
  [name: string]: UnsupportedBrowserScenario;
}

const browserToUBS: UnsupportedBrowserScenarioMap = {
  'Google Chrome': UnsupportedBrowserScenario.UNSUPPORTED_CHROME,
  'Mozilla Firefox': UnsupportedBrowserScenario.UNSUPPORTED_FIREFOX,
  'Microsoft Edge': UnsupportedBrowserScenario.UNSUPPORTED_EDGE,
  'Samsung Internet': UnsupportedBrowserScenario.UNSUPPORTED_SAMSUNG_INTERNET,
  Opera: UnsupportedBrowserScenario.UNSUPPORTED_OPERA,
  Safari: UnsupportedBrowserScenario.UNSUPPORTED_SAFARI,
};

const osToUBS: UnsupportedBrowserScenarioMap = {
  Windows: UnsupportedBrowserScenario.UNSUPPORTED_WINDOWS_BROWSER,
  macOS: UnsupportedBrowserScenario.UNSUPPORTED_MACOS_BROWSER,
  iOS: UnsupportedBrowserScenario.UNSUPPORTED_IOS_BROWSER,
  Android: UnsupportedBrowserScenario.UNSUPPORTED_ANDROID_BROWSER,
};

type ScenarioToBrowserMap = {
  [key in UnsupportedBrowserScenario]: string;
};

const ubsToBrowserMap = Object.keys(browserToUBS).reduce((a, k) => {
  a[browserToUBS[k]] = k;
  return a;
}, {} as ScenarioToBrowserMap);
export const ubsToBrowser = (ubs: UnsupportedBrowserScenario) => ubsToBrowserMap[ubs];

type SupportedBrowsers = {
  [id: string]: { [id: string]: string };
};

export const getSupportedBrowsers = () => {
  const browserClass: BrowserBehavior = new DefaultBrowserBehavior();
  const supportString: string = browserClass.supportString();
  const browserRegExp = new RegExp('^(.+?) ((' + supportedOS.join('|') + ') )?(\\d+)');

  const supportedBrowsers: SupportedBrowsers = {
    default: {},
  };
  supportString.split(', ').forEach((browser) => {
    const browserMatch = browserRegExp.exec(browser);

    if (browserMatch) {
      const [, browser, os, , version]: string[] = browserMatch;

      if (os) {
        if (!(os in supportedBrowsers)) {
          supportedBrowsers[os] = {};
        }
        supportedBrowsers[os][browser] = version;
      } else {
        supportedBrowsers['default'][browser] = version;
      }
    }
  });

  return supportedBrowsers;
};

const browserIsSupported = (browser: any) => {
  const supportedBrowsers: SupportedBrowsers = getSupportedBrowsers();
  if (browser.getOSName() in supportedBrowsers) {
    return getBrowserName() in supportedBrowsers[browser.getOSName()];
  } else {
    return getBrowserName() in supportedBrowsers['default'];
  }
};

/**
 * Returns an UnsupportedBrowserScenario (UBS) which represents a specific unsupported
 * browser scenario.  There can be multiple unsupported browser scenarios per browser
 * which is why we're using UBS's instead of just the browser name
 */
export const getBrowserSupportKey = () => {
  const browser: Parser.Parser = getParser(window.navigator.userAgent);
  const browserClass: BrowserBehavior = new DefaultBrowserBehavior();

  if (getBrowserName() === 'Internet Explorer') {
    // Trigger Internet Explorer warning
    return UnsupportedBrowserScenario.INTERNET_EXPLORER;
  } else if (!browserClass.isSupported()) {
    // Check if browser is supported, if not then determine whether this is an unsupported version of a supported browser
    const os = browser.getOSName();
    if (!supportedOS.includes(os)) {
      // Default to Windows message if we don't recognize the OS
      return UnsupportedBrowserScenario.UNSUPPORTED_WINDOWS_BROWSER;
    }
    const browserName = getBrowserName();
    if (browserIsSupported(browser)) {
      // Unsupported version of supported browser
      return browserToUBS[browserName];
    } else {
      // Unsupported browser
      return osToUBS[os];
    }
  }
  return null;
};

export const isUnsupportedVersion = (scenario: UnsupportedBrowserScenario) =>
  unSupportedVersionScenarios.indexOf(scenario) > -1;

export const getPlatformType = () => getParser(window.navigator.userAgent).getPlatformType();

export const isUnsupportedBrowserOfOS = (scenario: UnsupportedBrowserScenario) =>
  unsupportedBrowserOfOSScenarios.indexOf(scenario) > -1;

// End Unsupported Browser Code

export const getOsBrowser = () => {
  const browser = getParser(window.navigator.userAgent);
  let osName = browser.getOSName().toUpperCase();
  /**
   * This will help detect iPad because it reports a MacOS UA. We will update this when Apple releases a
   * touchscreen MacOS device.
   */
  if (osName === 'MACOS' && window.navigator.maxTouchPoints > 0) {
    osName = 'IOS';
  }

  return `${osName}_${browser.getBrowserName().toUpperCase().split(' ').join('_')}`;
};

/**
 * Is an iPhone XR, XS, XS Max, or X
 */
export const isIphoneXSafari = () => {
  const browser = getParser(window.navigator.userAgent);
  const isIPhone = browser.getPlatform().model === 'iPhone';
  // ex: "828x1792"
  const resolution = `${window.screen.width * window.devicePixelRatio}x${
    window.screen.height * window.devicePixelRatio
  }`;
  /* possible physical resolutions are:
  iPhone XR (2018)	828 x 1792
  iPhone XS Max (2018)	1242 x 2688
  iPhone XS (2018)	1125 x 2436
  iPhone X (2017)	1125 x 2436
  */
  return isIPhone && ['828x1792', '1242x2688', '1125x2436'].includes(resolution);
};

export enum RtcPermission {
  Prompt = 'Prompt',
  Deny = 'Deny',
  Allow = 'Allow',
}

export function getRtcPermission(devicePermission: string) {
  let rtcPermission: RtcPermission;
  switch (devicePermission) {
    case 'DENIED':
      rtcPermission = RtcPermission.Deny;
      break;
    case 'IN_PROGRESS':
      rtcPermission = RtcPermission.Prompt;
      break;
    default:
      rtcPermission = RtcPermission.Allow;
      break;
  }

  return rtcPermission;
}

export const sleep = (ms: number) => {
  return new Promise<number>((resolve) =>
    setTimeout(() => {
      resolve(-1);
    }, ms)
  );
};
